import { z } from "zod";

// Tips: a visitor buys the creator a few of their "coffees" from a Tip element, on the creator's
// own Stripe account, with no account of their own, and may leave a name and a message. The
// website's settings say what one is and costs (Customize › Layout › Tips), so the price goes
// live with the site's one Publish and checkout charges what the published page shows. The
// creator reads every tip in Members › Supporters; a Supporters element shows the public ones.
// See docs.drate.app/drate7-competitor-templates#tip-jar and api/src/tips.

export const tipCurrencies = ["usd", "eur", "gbp", "cad", "aud"] as const;
export type TipCurrency = (typeof tipCurrencies)[number];

/** The most one tip can be, whatever its unit: a guard against mistakes and stolen cards. */
export const tipMaxCents = 100_000;
/** The most units a supporter can choose. */
export const tipMaxUnits = 100;

const presetUnits = z.number().int().min(1).max(tipMaxUnits);

/** What a tip is on this site: its unit, what one costs and how many a supporter picks from. */
export const tipSettingsSchema = z.object({
  /** One of what a supporter buys, singular: "coffee", "slice of pizza", "brush". */
  unit: z.string().trim().min(1).max(30).catch("coffee").default("coffee"),
  /** Its plural, when adding an "s" isn't it ("slices of pizza"); empty adds the "s". */
  plural: z.string().trim().max(40).catch("").default(""),
  emoji: z.string().trim().max(8).catch("☕").default("☕"),
  unitCents: z.number().int().min(100).max(tipMaxCents).catch(500).default(500),
  currency: z.enum(tipCurrencies).catch("usd").default("usd"),
  /** The quick choices beside the custom count, smallest first. */
  presets: z
    .array(presetUnits)
    .min(1)
    .max(4)
    .transform((values) => [...new Set(values)].sort((a, b) => a - b))
    .catch([1, 3, 5])
    .default([1, 3, 5]),
  /** What a supporter reads once their tip goes through. */
  thanks: z
    .string()
    .trim()
    .max(300)
    .catch("Thank you so much for the support!")
    .default("Thank you so much for the support!"),
});
export type TipSettings = z.infer<typeof tipSettingsSchema>;
export const defaultTipSettings: TipSettings = tipSettingsSchema.parse({});

/** The site's tip settings, or the defaults when it has none or they no longer parse. */
export function tipSettingsOf(value: unknown): TipSettings {
  const parsed = tipSettingsSchema.safeParse(value ?? {});
  return parsed.success ? parsed.data : defaultTipSettings;
}

/** "1 coffee", "3 coffees". */
export function tipUnits(settings: Pick<TipSettings, "unit" | "plural">, count: number) {
  const word = count === 1 ? settings.unit : settings.plural || `${settings.unit}s`;
  return `${count} ${word}`;
}

export { tipMoney } from "./tip-money";

/** The most units a supporter can pick at this unit price. */
export function tipUnitLimit(settings: Pick<TipSettings, "unitCents">) {
  return Math.max(1, Math.min(tipMaxUnits, Math.floor(tipMaxCents / settings.unitCents)));
}

const pagePath = z
  .string()
  .max(200)
  .regex(/^\/(?!\/)[^?#]*$/)
  .refine(
    (path) =>
      !Array.from(path).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127),
  )
  .catch("/")
  .default("/");

/**
 * A supporter's tip from a Tip element: how many units, and the name and message they leave.
 * The price comes from the published website, never from here. `website` is the hidden trap
 * field bots fill. Form posts send strings, a checkbox's "on", and the typed count as `custom`
 * beside the chosen preset; the typed count wins. `anchor` is the element to return to.
 */
export const tipCheckoutInput = z
  .object({
    units: z.coerce.number().int().min(1).max(tipMaxUnits),
    custom: z.union([z.literal(""), z.coerce.number().int().min(1).max(tipMaxUnits)]).optional(),
    name: z.string().trim().max(60).default(""),
    message: z.string().trim().max(500).default(""),
    private: z
      .union([z.boolean(), z.literal("on"), z.literal("true"), z.literal("")])
      .transform((value) => value === true || value === "on" || value === "true")
      .default(false),
    pagePath,
    anchor: z
      .string()
      .regex(/^[A-Za-z0-9_-]{1,100}$/)
      .optional()
      .catch(undefined),
    website: z.string().max(1000).default(""),
  })
  .transform(({ custom, ...input }) => ({
    ...input,
    units: typeof custom === "number" ? custom : input.units,
  }));
export type TipCheckoutInput = z.infer<typeof tipCheckoutInput>;

/** A public tip on a Supporters element: no email, no amount, nothing private. */
export const publicTip = z.object({
  id: z.string(),
  name: z.string(),
  units: z.number(),
  unit: z.string(),
  plural: z.string(),
  emoji: z.string(),
  message: z.string(),
  paidAt: z.string(),
});
export type PublicTip = z.infer<typeof publicTip>;
export const publicTipList = z.object({ items: z.array(publicTip), supporters: z.number() });
export type PublicTipList = z.infer<typeof publicTipList>;

/** How many tips a Supporters element shows. */
export const supportersLimits = [3, 6, 10, 20] as const;

/** A tip as the creator reads it in Members › Supporters. */
export const creatorTip = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  units: z.number(),
  unit: z.string(),
  plural: z.string(),
  emoji: z.string(),
  cents: z.number(),
  feeCents: z.number(),
  currency: z.string(),
  message: z.string(),
  private: z.boolean(),
  hidden: z.boolean(),
  pagePath: z.string(),
  paidAt: z.string(),
  readAt: z.string().nullable(),
});
export type CreatorTip = z.infer<typeof creatorTip>;

export const tipListInput = z.object({
  creatorId: z.uuid(),
  search: z.string().trim().max(100).default(""),
  offset: z.number().int().min(0).max(1_000_000).default(0),
  limit: z.number().int().min(1).max(500).default(50),
});
export const tipTotal = z.object({ currency: z.string(), cents: z.number(), feeCents: z.number() });
export const tipPage = z.object({
  items: z.array(creatorTip),
  total: z.number(),
  unread: z.number(),
  supporters: z.number(),
  totals: z.array(tipTotal),
});
export type TipPage = z.infer<typeof tipPage>;

/** What a supporter's return from checkout learns: whether it went through. */
export const tipStatus = z.enum(["pending", "paid", "expired", "refunded"]);
export type TipStatus = z.infer<typeof tipStatus>;

/** Sent on the window once a returning supporter's tip went through, for a Supporters list. */
export const tipPaidEvent = "drate:tip-paid";

/** A name left empty, on the wall and in the dashboard. */
export const anonymousSupporter = "Someone";
